/**
 * TVChart — TradingView Lightweight Charts wrapper
 * Renders a dark-themed area/line chart for price data.
 * Props:
 *   data        [{time: ISOString, close: number, ...}]  price bars
 *   signals     [{time: ISOString, direction: 'buy'|'sell'}]  optional markers
 *   height      number  chart height in px (default 220)
 *   color       string  line / area color hex (default #3b82f6)
 *   showVolume  bool    render volume bars in lower pane (default false)
 */
import { useEffect, useRef } from 'react'
import { createChart, CrosshairMode } from 'lightweight-charts'

const DARK = {
  layout:          { background: { color: '#0f172a' }, textColor: '#475569' },
  grid:            { vertLines: { color: '#1e293b' }, horzLines: { color: '#1e293b' } },
  crosshair:       { mode: CrosshairMode.Normal },
  rightPriceScale: { borderColor: '#1e293b', textColor: '#64748b' },
  timeScale:       { borderColor: '#1e293b', timeVisible: true, secondsVisible: false },
  handleScroll:    true,
  handleScale:     true,
}

function toUnix(isoStr) {
  return Math.floor(new Date(isoStr).getTime() / 1000)
}

export default function TVChart({ data = [], signals = [], height = 220, color = '#3b82f6', showVolume = false }) {
  const containerRef = useRef(null)
  const chartRef     = useRef(null)
  const seriesRef    = useRef(null)
  const volRef       = useRef(null)

  // Build / update chart when data changes
  useEffect(() => {
    if (!containerRef.current) return
    if (!data.length) return

    // Create chart if it doesn't exist yet
    if (!chartRef.current) {
      chartRef.current = createChart(containerRef.current, {
        ...DARK,
        width:  containerRef.current.clientWidth,
        height,
      })

      seriesRef.current = chartRef.current.addAreaSeries({
        lineColor:   color,
        topColor:    color + '44',
        bottomColor: color + '00',
        lineWidth:   1.5,
        priceLineVisible: false,
      })

      if (showVolume) {
        volRef.current = chartRef.current.addHistogramSeries({
          color:         '#1e40af66',
          priceFormat:   { type: 'volume' },
          priceScaleId:  'vol',
          scaleMargins:  { top: 0.8, bottom: 0 },
        })
        chartRef.current.priceScale('vol').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } })
      }
    }

    // Map and sort data
    const priceData = data
      .map(d => ({ time: toUnix(d.time || d.timestamp), value: Number(d.close ?? d.value) }))
      .filter(d => !isNaN(d.value) && d.value > 0)
      .sort((a, b) => a.time - b.time)
      .filter((d, i, arr) => i === 0 || d.time !== arr[i - 1].time) // dedupe

    if (!priceData.length) return
    seriesRef.current.setData(priceData)

    if (showVolume && volRef.current) {
      const volData = data
        .map(d => ({ time: toUnix(d.time || d.timestamp), value: Number(d.volume ?? 0), color: '#1e40af55' }))
        .filter(d => d.value > 0)
        .sort((a, b) => a.time - b.time)
        .filter((d, i, arr) => i === 0 || d.time !== arr[i - 1].time)
      volRef.current.setData(volData)
    }

    // Add BUY/SELL markers
    if (signals.length) {
      const markers = signals
        .map(s => ({
          time:     toUnix(s.time || s.timestamp),
          position: (s.direction || '').toLowerCase() === 'buy' ? 'belowBar' : 'aboveBar',
          color:    (s.direction || '').toLowerCase() === 'buy' ? '#10b981' : '#f43f5e',
          shape:    (s.direction || '').toLowerCase() === 'buy' ? 'arrowUp'  : 'arrowDown',
          text:     s.symbol || '',
        }))
        .sort((a, b) => a.time - b.time)
      seriesRef.current.setMarkers(markers)
    }

    chartRef.current.timeScale().fitContent()
  }, [data, signals, color, height, showVolume])

  // Resize observer
  useEffect(() => {
    if (!containerRef.current) return
    const ro = new ResizeObserver(() => {
      if (chartRef.current && containerRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth })
      }
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.remove()
        chartRef.current  = null
        seriesRef.current = null
        volRef.current    = null
      }
    }
  }, [])

  if (!data.length) {
    return (
      <div
        className="flex items-center justify-center rounded-lg"
        style={{ height, background: '#0f172a', border: '1px dashed #1e293b' }}
      >
        <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', color: '#334155', letterSpacing: '0.08em' }}>
          NO DATA — ENTER A SYMBOL AND CLICK LOOK UP
        </p>
      </div>
    )
  }

  return <div ref={containerRef} style={{ width: '100%', height }} />
}
